import { FUNCTION_BASE } from "../lib/constants";
import { authFetch } from "../lib/api";
import React, { useEffect, useState, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import {
  FaHome, FaUsers, FaClipboardList, FaChartBar,
  FaDollarSign, FaCog, FaChevronLeft, FaChevronRight,
  FaChevronDown, FaFacebook, FaInstagram,
  FaFileAlt, FaSignOutAlt, FaCheckCircle, FaChartLine,
  FaMousePointer, FaCreditCard, FaSyncAlt, FaPlus,
  FaDiscord, FaQuestionCircle, FaBell, FaTimes,
} from "react-icons/fa";
import CreateCampaignWizard from "../components/CreateCampaignWizard";
import ManageCampaigns from "./ManageCampaigns";
import ReportsPage from "./ReportsPage";
import AudiencesPage from "./AudiencesPage";
import BillingPage from "./BillingPage";
import SettingsPage from "./SettingsPage";
import { db, auth } from "../lib/firebase";
import { sendEmailVerification } from "firebase/auth";
import { doc, getDoc, collection, getDocs, query, limit, where, setDoc } from "firebase/firestore";
import "../styles/Dashboard.css";

const Dashboard = ({ onLogout }) => {

  const [campaigns, setCampaigns] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [currentView, setCurrentView] = useState(searchParams.get("view") || "dashboard");
  const [draftInfo, setDraftInfo] = useState(null);

  useEffect(() => {
    try {
      const saved = localStorage.getItem("wizardDraft");
      if (saved) {
        const d = JSON.parse(saved);
        if (d.campaignTitle || d.currentStep > 1) {
          setDraftInfo({ title: d.campaignTitle || "Untitled Campaign", step: d.currentStep || 1 });
        }
      }
    } catch (e) {}
  }, []);

  const navigateTo = (view) => {
    setCurrentView(view);
    setSearchParams({ view });
  };

  useEffect(() => {
    const titles = {
      dashboard: "Overview", wizard: "Create Campaign",
      manageCampaigns: "Manage Campaigns", audiences: "Audiences",
      reports: "Reports", billing: "Billing", settings: "Settings",
    };
    document.title = `${titles[currentView] || "Dashboard"} — Adsoar`;
  }, [currentView]);

  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizardInitialData, setWizardInitialData] = useState(null);
  const [fbConnected, setFbConnected] = useState(false);
  const [fbAccounts, setFbAccounts] = useState({});
  const [fbProfile, setFbProfile] = useState(null);
  const [selectedAdAccount, setSelectedAdAccount] = useState(null);
  const [showFbSuccess, setShowFbSuccess] = useState(false);
  const [emailVerified, setEmailVerified] = useState(true);
  const [verifyEmailStatus, setVerifyEmailStatus] = useState("");
  const [showNotifDropdown, setShowNotifDropdown] = useState(false);
  const [dismissedNotifIds, setDismissedNotifIds] = useState(() => {
    try { return JSON.parse(localStorage.getItem("dismissedNotifIds") || "[]"); } catch { return []; }
  });
  const notifPopoverRef = useRef(null);

  useEffect(() => {
    try { localStorage.setItem("dismissedNotifIds", JSON.stringify(dismissedNotifIds)); } catch {}
  }, [dismissedNotifIds]);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => (
    typeof window !== "undefined" && window.matchMedia("(max-width: 768px)").matches
  ));

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 768px)");
    const handleChange = (e) => { if (e.matches) setSidebarCollapsed(true); };
    mq.addEventListener("change", handleChange);
    return () => mq.removeEventListener("change", handleChange);
  }, []);
  const [fbCampaigns, setFbCampaigns] = useState([]);
  const [fbInsights, setFbInsights] = useState(null);
  const [loadingInsights, setLoadingInsights] = useState(false);
  const [insightsError, setInsightsError] = useState(null);
  const [accountsError, setAccountsError] = useState(null);
  const [fbPages, setFbPages] = useState([]);
  const [instagramAccounts, setInstagramAccounts] = useState([]);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [showConnPopover, setShowConnPopover] = useState(false);
  const [settingsDefaultTab, setSettingsDefaultTab] = useState("account");
  const connPopoverRef = useRef(null);

  const [showFeedback, setShowFeedback] = useState(false);
  const [feedbackMsg, setFeedbackMsg] = useState("");
  const [feedbackSent, setFeedbackSent] = useState(false);
  const [activeMetaTab, setActiveMetaTab] = useState("adAccounts");
  const META_TABS = [
    { key: "adAccounts", label: "Ad Accounts" },
    { key: "pages", label: "Facebook Pages" },
    { key: "instagram", label: "Instagram" },
  ];

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user) { setCurrentUser(user); setEmailVerified(user.emailVerified); }
      else { setCurrentUser(null); setCampaigns([]); }
    });
    return () => unsubscribe();
  }, []);

  const handleResendVerification = async () => {
    if (!auth.currentUser) return;
    setVerifyEmailStatus("sending");
    try {
      await sendEmailVerification(auth.currentUser);
      setVerifyEmailStatus("sent");
    } catch (err) {
      setVerifyEmailStatus(err.code === "auth/too-many-requests" ? "rate-limited" : "error");
    }
  };

  const handleRefreshVerification = async () => {
    if (!auth.currentUser) return;
    await auth.currentUser.reload();
    setEmailVerified(auth.currentUser.emailVerified);
  };

  useEffect(() => {
    const handleMessage = (event) => {
      if (event.origin !== "https://adsoar.cc") return;
      if (event.data.type === 'FB_AUTH_SUCCESS') {
        setShowFbSuccess(true);
        if (currentUser) loadData();
        setTimeout(() => setShowFbSuccess(false), 5000);
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [currentUser]);

  const loadData = async () => {
    if (!currentUser) return;
    try {
      const campaignsQuery = query(collection(db, "campaigns"), where("userId", "==", currentUser.uid), limit(5));
      const cs = await getDocs(campaignsQuery);
      setCampaigns(cs.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (e) { console.warn("Campaigns query failed:", e.message); }
    const userSnap = await getDoc(doc(db, "users", currentUser.uid));
    if (userSnap.exists()) {
      const userData = userSnap.data();
      if (userData.fbAccessToken) {
        setFbConnected(true);
        setFbAccounts(userData.fbAccounts || {});
        const preferredAccount = "act_337230608";
        const accounts = userData.fbAccounts || {};
        const defaultAccount = userData.selectedAdAccount ||
          (accounts[preferredAccount] ? preferredAccount : Object.keys(accounts)[0] || null);
        setSelectedAdAccount(defaultAccount);
        // Auto-save the default account to Firestore if it wasn't already saved
        if (!userData.selectedAdAccount && defaultAccount) {
          try {
            await setDoc(doc(db, "users", currentUser.uid), { selectedAdAccount: defaultAccount }, { merge: true });
          } catch (e) { console.warn("Could not auto-save default ad account:", e); }
        }
        setFbCampaigns(userData.fbCampaigns || []);
        setFbPages(userData.fbPages || []);
        setInstagramAccounts(userData.instagramAccounts || []);
        // Silently refresh pages data in background (fixes stale follower counts + expired pictures)
        authFetch(`${FUNCTION_BASE}/api/fb/pages/${currentUser.uid}`)
          .then(r => r.json())
          .then(data => {
            if (data.success && data.pages) {
              setFbPages(data.pages.map(p => ({
                id: p.id,
                name: p.name,
                category: p.category,
                accessToken: p.access_token,
                picture: p.picture?.data?.url,
                fanCount: p.fan_count,
                followersCount: p.followers_count,
                newLikeCount: p.new_like_count,
                talkingAboutCount: p.talking_about_count,
              })));
            }
          })
          .catch(err => console.error("Background pages refresh failed:", err));
        // Show cached profile immediately, then refresh in background (CDN URLs expire)
        setFbProfile(userData.fbProfile || null);
        authFetch(`${FUNCTION_BASE}/api/fb/refresh-profile`, {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ userId: currentUser.uid })
        })
          .then(r => r.json())
          .then(data => { if (data.success && data.profile) setFbProfile(data.profile); })
          .catch(err => console.error("Background profile refresh failed:", err));
        if (defaultAccount) fetchInsights(currentUser.uid);
      }
    }
  };

  const fetchInsights = async (userId) => {
    setLoadingInsights(true);
    setInsightsError(null);
    try {
      const response = await authFetch(`${FUNCTION_BASE}/api/fb/insights/${userId}`);
      const data = await response.json();
      if (data.success) {
        setFbInsights(data);
        if (data.campaigns) setFbCampaigns(data.campaigns);
      } else {
        setInsightsError(data.error || "Couldn't load your ad performance data.");
      }
    } catch (err) {
      console.error("Failed to fetch insights:", err);
      setInsightsError("Couldn't load your ad performance data. Check your connection and try again.");
    }
    finally { setLoadingInsights(false); }
  };

  useEffect(() => { loadData(); }, [currentUser]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (connPopoverRef.current && !connPopoverRef.current.contains(e.target)) {
        setShowConnPopover(false);
      }
      if (notifPopoverRef.current && !notifPopoverRef.current.contains(e.target)) {
        setShowNotifDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const connectFacebook = async () => {
    if (!currentUser?.uid) return alert("User not logged in!");
    const width = 600, height = 700;
    const left = window.screen.width / 2 - width / 2;
    const top = window.screen.height / 2 - height / 2;
    // Open synchronously (inside the click handler) with a blank page first —
    // popup blockers kill window.open() calls that happen after an await.
    const popup = window.open("", "Facebook OAuth", `width=${width},height=${height},top=${top},left=${left}`);
    try {
      const startRes = await authFetch(`${FUNCTION_BASE}/api/fb/oauth/start`, { method: "POST" });
      const { state } = await startRes.json();
      if (popup) popup.location.href = `${FUNCTION_BASE}/api/fb/oauth?state=${state}`;
    } catch (err) {
      console.error("Failed to start Facebook connection:", err);
      if (popup) popup.close();
      alert("Failed to start connecting your Facebook account. Please try again.");
    }
  };

  const refreshAccounts = async () => {
    if (!currentUser?.uid || isRefreshing) return;
    setIsRefreshing(true);
    setAccountsError(null);
    try {
      const response = await authFetch(
        `${FUNCTION_BASE}/api/fb/refresh-accounts`,
        { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ userId: currentUser.uid }) }
      );
      const data = await response.json();
      if (data.success && data.fbAccounts) {
        setFbAccounts(data.fbAccounts);
      } else {
        setAccountsError(data.error || "Couldn't refresh your account data.");
      }
    } catch (err) {
      console.error("Failed to refresh accounts:", err);
      setAccountsError("Couldn't refresh your account data. Check your connection and try again.");
    }
    finally { setIsRefreshing(false); }
  };

  const switchAdAccount = async (accountId) => {
    setSelectedAdAccount(accountId);
    try {
      const userRef = doc(db, "users", currentUser.uid);
      await setDoc(userRef, { selectedAdAccount: accountId }, { merge: true });
      fetchInsights(currentUser.uid);
    } catch (err) { console.error("Error switching ad account:", err); }
  };

  const sidebarWidth = sidebarCollapsed ? 80 : 260;

  // Computed live from real state every render — nothing here is a persisted
  // "dismissed" flag, so it can never go stale or lie about what's actually
  // still unresolved (unverified email, an unfinished draft, etc).
  const notifications = [];
  if (!emailVerified) {
    notifications.push({ id: "verify-email", type: "verify-email" });
  }
  if (draftInfo) {
    // Content-specific id: dismissing this draft's notification shouldn't
    // permanently hide the notification for a later, different draft.
    notifications.push({ id: `draft-${draftInfo.title}`, type: "draft" });
  }
  const visibleNotifications = notifications.filter(n => !dismissedNotifIds.includes(n.id));

  return (
    <>
      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>

      {/* Sidebar collapse toggle */}
      <div
        className="sidebar-toggle"
        style={{ left: sidebarCollapsed ? 68 : 248 }}
        onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
        role="button"
        tabIndex={0}
        aria-label={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setSidebarCollapsed(!sidebarCollapsed); } }}
      >
        {sidebarCollapsed ? <FaChevronRight /> : <FaChevronLeft />}
      </div>

      <div className="dash-layout">
        {/* Sidebar */}
        <div
          className={`dash-sidebar${sidebarCollapsed ? " collapsed" : ""}`}
          style={{ width: sidebarWidth }}
        >
          <div>
            {/* Logo */}
            <div className={`sidebar-logo${sidebarCollapsed ? " collapsed" : ""}`} onClick={() => navigateTo("dashboard")} style={{ cursor: "pointer" }}>
              {sidebarCollapsed
                ? <span className="sidebar-logo-icon">A</span>
                : <span className="sidebar-logo-text">Ad<span>soar</span></span>
              }
            </div>

            {/* Nav */}
            <ul className="sidebar-nav">
              {[
                { view: "dashboard", icon: <FaHome />, label: "Overview" },
                { view: "manageCampaigns", icon: <FaClipboardList />, label: "Campaigns" },
                { view: "audiences", icon: <FaUsers />, label: "Audiences" },
                { view: "reports", icon: <FaChartBar />, label: "Reports" },
                { view: "billing", icon: <FaDollarSign />, label: "Billing" },
                { view: "settings", icon: <FaCog />, label: "Settings" },
              ].map(({ view, icon, label }) => (
                <li
                  key={view}
                  className={`sidebar-nav-item${currentView === view ? " active" : ""}${sidebarCollapsed ? " collapsed" : ""}`}
                  onClick={() => navigateTo(view)}
                  title={sidebarCollapsed ? label : ""}
                  role="button"
                  tabIndex={0}
                  aria-current={currentView === view ? "page" : undefined}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); navigateTo(view); } }}
                >
                  <span className="sidebar-nav-icon">{icon}</span>
                  {!sidebarCollapsed && label}
                </li>
              ))}
            </ul>
          </div>

          {/* Bottom: feedback + help + user info */}
          <div className="sidebar-bottom">
            <div className="sidebar-utility-btns">
              <a href="https://discord.gg/" target="_blank" rel="noreferrer" className="sidebar-util-btn" title="Join our Discord">
                <FaDiscord />
                {!sidebarCollapsed && <span>Community</span>}
              </a>
              <button className="sidebar-util-btn" title="Contact support" onClick={() => { setShowFeedback(true); setFeedbackSent(false); setFeedbackMsg(""); }}>
                <FaQuestionCircle />
                {!sidebarCollapsed && <span>Help & Support</span>}
              </button>
            </div>

            {!sidebarCollapsed ? (
              <div className="sidebar-user">
                <div className="sidebar-user-avatar">
                  {(currentUser?.displayName || "U")[0].toUpperCase()}
                </div>
                <div style={{ flex: 1, overflow: "hidden" }}>
                  <div className="sidebar-user-name">{currentUser?.displayName || "User"}</div>
                  <div className="sidebar-user-email">{currentUser?.email || ""}</div>
                </div>
              </div>
            ) : (
              <div className="sidebar-user-collapsed">
                <div className="sidebar-user-avatar" title={currentUser?.displayName || "User"}>
                  {(currentUser?.displayName || "U")[0].toUpperCase()}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Spacer */}
        <div style={{ width: sidebarWidth, minWidth: sidebarWidth, flexShrink: 0, transition: "width 0.3s ease, min-width 0.3s ease" }} />

        {/* Main */}
        <div className="dash-main">

          {/* Top bar */}
          <div className="dash-topbar">
            <div className="topbar-title">
              {currentView === "dashboard" && "Overview"}
              {currentView === "wizard" && "Create Campaign"}
              {currentView === "manageCampaigns" && "Campaigns"}
              {currentView === "audiences" && "Audiences"}
              {currentView === "reports" && "Reports"}
              {currentView === "billing" && "Billing"}
              {currentView === "settings" && "Settings"}
            </div>

            <div className="topbar-right">
              <div className="notif-wrap" ref={notifPopoverRef}>
                <button className="notif-bell-btn" onClick={() => setShowNotifDropdown(p => !p)} title="Notifications">
                  <FaBell style={{ fontSize: "0.95rem" }} />
                  {visibleNotifications.length > 0 && <span className="notif-badge">{visibleNotifications.length}</span>}
                </button>

                {showNotifDropdown && (
                  <div className="profile-dropdown notif-dropdown">
                    <div className="pd-header notif-header">
                      <div className="pd-user-name">Notifications</div>
                      {visibleNotifications.length > 0 && (
                        <button
                          className="notif-clear-all"
                          onClick={() => setDismissedNotifIds(prev => [...prev, ...notifications.map(n => n.id)])}
                        >
                          Clear All
                        </button>
                      )}
                    </div>
                    {visibleNotifications.length === 0 ? (
                      <div className="notif-empty">You're all caught up.</div>
                    ) : (
                      <div className="notif-list">
                        {visibleNotifications.map((n) => n.type === "verify-email" ? (
                          <div key={n.id} className="notif-item">
                            <button className="notif-item-dismiss" onClick={() => setDismissedNotifIds(prev => [...prev, n.id])} title="Dismiss">
                              <FaTimes />
                            </button>
                            <div className="notif-item-title">Verify your email</div>
                            <div className="notif-item-sub">Check your inbox for a link from Adsoar.</div>
                            {verifyEmailStatus === "sent" && <div className="notif-item-status">Email sent!</div>}
                            {verifyEmailStatus === "rate-limited" && <div className="notif-item-status">Too many requests — try again shortly.</div>}
                            {verifyEmailStatus === "error" && <div className="notif-item-status">Failed to send — try again.</div>}
                            <div className="notif-item-actions">
                              <button className="draft-btn-secondary" onClick={handleResendVerification} disabled={verifyEmailStatus === "sending"}>
                                {verifyEmailStatus === "sending" ? "Sending..." : "Resend Email"}
                              </button>
                              <button className="draft-btn-primary" onClick={handleRefreshVerification}>
                                I've Verified
                              </button>
                            </div>
                          </div>
                        ) : n.type === "draft" ? (
                          <div key={n.id} className="notif-item">
                            <button className="notif-item-dismiss" onClick={() => setDismissedNotifIds(prev => [...prev, n.id])} title="Dismiss">
                              <FaTimes />
                            </button>
                            <div className="notif-item-title">Unfinished campaign</div>
                            <div className="notif-item-sub">"{draftInfo.title}" — your answers are saved, pick up from Step 1</div>
                            <div className="notif-item-actions">
                              <button className="draft-btn-secondary" onClick={() => { localStorage.removeItem("wizardDraft"); setDraftInfo(null); }}>
                                Start New
                              </button>
                              <button className="draft-btn-primary" onClick={() => { setWizardOpen(true); setShowNotifDropdown(false); }}>
                                Continue
                              </button>
                            </div>
                          </div>
                        ) : null)}
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="profile-wrap" ref={connPopoverRef}>
                <div
                  className="profile-btn"
                  onClick={() => setShowConnPopover(p => !p)}
                  role="button"
                  tabIndex={0}
                  aria-haspopup="true"
                  aria-expanded={showConnPopover}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setShowConnPopover(p => !p); } }}
                >
                  {fbProfile?.picture
                    ? <img src={fbProfile.picture} alt="" className="profile-btn-pic" />
                    : <div className="profile-btn-avi">{(currentUser?.displayName || "U")[0].toUpperCase()}</div>
                  }
                  <span className="profile-btn-name">{currentUser?.displayName?.split(" ")[0] || "User"}</span>
                  <span className={fbConnected ? "profile-btn-dot profile-btn-dot-on" : "profile-btn-dot profile-btn-dot-off"} />
                  <FaChevronDown style={{ fontSize: "0.6rem", color: "#94a3b8" }} />
                </div>

                {showConnPopover && (
                  <div className="profile-dropdown">
                    {/* Adsoar account */}
                    <div className="pd-header">
                      <div className="pd-user-avi">{(currentUser?.displayName || "U")[0].toUpperCase()}</div>
                      <div>
                        <div className="pd-user-name">{currentUser?.displayName || "User"}</div>
                        <div className="pd-user-email">{currentUser?.email || ""}</div>
                      </div>
                    </div>

                    {/* Meta connection */}
                    <div className="pd-meta-section">
                      {fbConnected ? (
                        <>
                          <div className="pd-meta-label">
                            <span className="pd-dot pd-dot-on" /> Meta Connected
                          </div>
                          <div className="pd-meta-row">
                            {fbProfile?.picture
                              ? <img src={fbProfile.picture} alt="" className="pd-meta-pic" />
                              : <div className="pd-meta-avi">{fbProfile?.name?.[0]?.toUpperCase() || "F"}</div>
                            }
                            <span className="pd-meta-name">{fbProfile?.name || "Facebook User"}</span>
                          </div>
                          {Object.keys(fbAccounts).length > 0 && (
                            <div className="pd-account-current">
                              <span>{fbAccounts[selectedAdAccount]?.name || "No account selected"}</span>
                              {Object.keys(fbAccounts).length > 1 && (
                                <button
                                  className="pd-account-switch-link"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigateTo("dashboard");
                                    setActiveMetaTab("adAccounts");
                                    setShowConnPopover(false);
                                  }}
                                >
                                  Switch account
                                </button>
                              )}
                            </div>
                          )}
                        </>
                      ) : (
                        <>
                          <div className="pd-meta-label">
                            <span className="pd-dot pd-dot-off" /> Meta not connected
                          </div>
                          <button className="pd-connect-btn" onClick={(e) => { e.stopPropagation(); connectFacebook(); setShowConnPopover(false); }}>
                            Connect Meta
                          </button>
                        </>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="pd-actions">
                      {fbConnected && (
                        <button className="pd-action" onClick={() => { setSettingsDefaultTab("connections"); navigateTo("settings"); setShowConnPopover(false); }}>
                          <FaSyncAlt style={{ fontSize: "0.72rem" }} /> Manage Connections
                        </button>
                      )}
                      {fbConnected && (
                        <button className="pd-action" onClick={() => { connectFacebook(); setShowConnPopover(false); }}>
                          <FaCheckCircle style={{ fontSize: "0.72rem" }} /> Reconnect Meta
                        </button>
                      )}
                      <button className="pd-action" onClick={() => { navigateTo("settings"); setShowConnPopover(false); }}>
                        <FaCog style={{ fontSize: "0.72rem" }} /> Settings
                      </button>
                    </div>

                    {/* Sign out */}
                    <div className="pd-signout-wrap">
                      <button className="pd-signout" onClick={async () => {
                        await auth.signOut();
                        setCurrentUser(null);
                        setCurrentView("dashboard");
                        if (onLogout) onLogout();
                        setShowConnPopover(false);
                      }}>
                        <FaSignOutAlt style={{ fontSize: "0.72rem" }} /> Sign Out
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* FB Success banner */}
          {showFbSuccess && (
            <div className="dash-success-banner">
              <FaCheckCircle style={{ fontSize: "1.1rem", color: "#059669" }} />
              Your Facebook account has been connected successfully!
            </div>
          )}

          {/* Content */}
          <div className="dash-content">
            {currentView === "dashboard" && (
              <>
                <div className="dash-inner">
                <div className="dash-welcome-row">
                  <div className="dash-welcome">
                    <h1>Welcome back, {currentUser?.displayName?.split(' ')[0] || "User"}</h1>
                    <p>Here's what's happening with your campaigns today.</p>
                  </div>
                  <button className="create-campaign-btn" onClick={() => setWizardOpen(true)}>
                    <FaPlus style={{ fontSize: "0.8rem" }} /> Create Campaign
                  </button>
                </div>

                <div className="dash-overview-panel">

                {/* Last 30 Days performance metrics — real data, top of page */}
                <div className="dash-kpi-section">
                  <p className="tab-body-subtitle">Last 30 days</p>
                  {(() => {
                    const acct = fbInsights?.accountInsights?.[0];
                    const items = [
                      { title: "Spend", value: acct ? `$${parseFloat(acct.spend || 0).toFixed(2)}` : "--", icon: <FaDollarSign />, color: "#64748b" },
                      { title: "Impressions", value: acct ? parseInt(acct.impressions || 0).toLocaleString() : "--", icon: <FaChartBar />, color: "#64748b" },
                      { title: "Clicks", value: acct ? parseInt(acct.clicks || 0).toLocaleString() : "--", icon: <FaMousePointer />, color: "#64748b" },
                      { title: "CTR", value: acct?.ctr ? `${parseFloat(acct.ctr).toFixed(2)}%` : "--", icon: <FaChartLine />, color: "#64748b" },
                      { title: "CPC", value: acct?.cpc ? `$${parseFloat(acct.cpc).toFixed(2)}` : "--", icon: <FaCreditCard />, color: "#64748b" },
                      { title: "Reach", value: acct ? parseInt(acct.reach || 0).toLocaleString() : "--", icon: <FaUsers />, color: "#64748b" },
                    ];
                    return (
                      <div className="kpi-strip">
                        {items.map((item, i) => (
                          <div key={i} className="kpi-item">
                            <div className="kpi-icon" style={{ color: item.color }}>{item.icon}</div>
                            <div>
                              <div className="kpi-label">{item.title}</div>
                              <div className="kpi-value">{item.value}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    );
                  })()}
                </div>

                {/* Ad Performance (chart, ~65%) + Recent Campaigns (~35%) */}
                <div className="dash-body-grid">
                <div className="dash-col-main">
                  <div className="section-card">
                    <div className="section-card-header" style={{ cursor: "default" }}>
                      <div className="section-card-title-row">
                        <FaFacebook style={{ color: "#1877f2", fontSize: "1.1rem" }} />
                        <h2 className="section-card-title">Ad Performance</h2>
                      </div>
                      <button
                        className="insights-refresh-btn"
                        onClick={() => currentUser && fetchInsights(currentUser.uid)}
                        disabled={loadingInsights}
                      >
                        {loadingInsights ? "Loading..." : "Refresh Insights"}
                      </button>
                    </div>
                    {insightsError && (
                      <div className="dash-error-banner">
                        <span>{insightsError}</span>
                        <button onClick={() => currentUser && fetchInsights(currentUser.uid)}>Try again</button>
                      </div>
                    )}
                    {!fbConnected ? (
                      <div className="campaigns-empty">
                        <FaChartLine style={{ fontSize: "1.8rem", color: "#94a3b8" }} />
                        <p>Connect your Facebook account to see performance</p>
                        <span>Spend by campaign will show up here once connected</span>
                      </div>
                    ) : (() => {
                      const chartCampaigns = (fbInsights?.campaigns || [])
                        .map(c => ({ name: c.name, spend: parseFloat(c.insights?.data?.[0]?.spend || 0) }))
                        .filter(c => c.spend > 0)
                        .sort((a, b) => b.spend - a.spend)
                        .slice(0, 6);
                      if (chartCampaigns.length === 0) {
                        return (
                          <div className="campaigns-empty">
                            <FaChartLine style={{ fontSize: "1.8rem", color: "#94a3b8" }} />
                            <p>No spend data yet</p>
                            <span>Click "Refresh Insights" once your campaigns have spend to see them here</span>
                          </div>
                        );
                      }
                      const maxSpend = Math.max(...chartCampaigns.map(c => c.spend), 1);
                      return (
                        <>
                          <p className="tab-body-subtitle">Spend by campaign</p>
                          <div className="perf-chart">
                            {chartCampaigns.map((c, i) => (
                              <div key={i} className="perf-chart-row">
                                <span className="perf-chart-label" title={c.name}>{c.name}</span>
                                <div className="perf-chart-bar-track">
                                  <div className="perf-chart-bar" style={{ width: `${(c.spend / maxSpend) * 100}%` }} />
                                </div>
                                <span className="perf-chart-value">${c.spend.toFixed(2)}</span>
                              </div>
                            ))}
                          </div>
                        </>
                      );
                    })()}
                  </div>
                </div> {/* end dash-col-main */}

                <div className="dash-col-side">
                {/* Recent Campaigns */}
                <div className="recent-campaigns-card">
                  <div className="recent-campaigns-header">
                    <h2 className="recent-campaigns-title">Recent Campaigns</h2>
                    <button className="view-all-btn" onClick={() => navigateTo("manageCampaigns")}>View All →</button>
                  </div>
                  {campaigns.length === 0 ? (
                    <div className="campaigns-empty campaigns-empty-compact">
                      <FaClipboardList style={{ fontSize: "1.3rem", color: "#94a3b8" }} />
                      <p>No campaigns created in Adsoar yet</p>
                      <span>Campaigns you publish here will show up in this list</span>
                    </div>
                  ) : (
                    <ul className="recent-campaigns-list">
                      {campaigns.map((c, idx) => (
                        <li key={c.id} className="recent-campaign-item">
                          <div>
                            <div className="recent-campaign-name">{c.title}</div>
                            <div className="recent-campaign-date">{c.updatedAt?.toDate ? c.updatedAt.toDate().toLocaleString() : "—"}</div>
                          </div>
                          <span className={`recent-campaign-status ${c.status === "active" ? "recent-status-active" : "recent-status-default"}`}>
                            {c.status}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                </div> {/* end dash-col-side */}
                </div> {/* end dash-body-grid */}

                {/* Meta Account management — secondary to performance */}
                <div className="section-card dash-meta-account">
                  <div className="section-card-header" style={{ cursor: "default" }}>
                    <div className="section-card-title-row">
                      <h2 className="section-card-title">Meta Account</h2>
                    </div>
                    {fbConnected && (
                      <button className="refresh-btn" onClick={refreshAccounts} disabled={isRefreshing} title="Refresh payment status & balance">
                        <FaSyncAlt style={{ fontSize: "0.7rem", animation: isRefreshing ? "spin 0.7s linear infinite" : "none" }} />
                        {isRefreshing ? "Refreshing..." : "Refresh"}
                      </button>
                    )}
                  </div>

                  {accountsError && (
                    <div className="dash-error-banner">
                      <span>{accountsError}</span>
                      <button onClick={refreshAccounts}>Try again</button>
                    </div>
                  )}

                  {!fbConnected ? (
                    <div className="fb-connect-card" style={{ margin: 0 }}>
                      <div className="fb-connect-icon"><FaFacebook /></div>
                      <h3>Connect Your Facebook Account</h3>
                      <p>Link your Facebook Business account to start managing campaigns</p>
                      <button className="fb-connect-btn" onClick={connectFacebook}>Connect Facebook</button>
                    </div>
                  ) : (
                    <>
                      <div className="fb-account-row" style={{ marginBottom: 16 }}>
                        {fbProfile?.picture
                          ? <img src={fbProfile.picture} alt={fbProfile?.name || "FB"} className="fb-account-pic" />
                          : <div className="fb-account-pic-fallback">{fbProfile?.name?.[0]?.toUpperCase() || "F"}</div>
                        }
                        <div className="fb-account-info">
                          <FaFacebook style={{ color: "#1877f2", fontSize: "0.9rem" }} />
                          <span className="fb-account-name">{fbProfile?.name || "Facebook User"}</span>
                          <span className="fb-connected-badge"><FaCheckCircle style={{ fontSize: "0.6rem" }} /> Connected</span>
                          {Object.keys(fbAccounts).length > 0 && (
                            <span className="fb-account-count">· {Object.keys(fbAccounts).length} ad account{Object.keys(fbAccounts).length > 1 ? "s" : ""}</span>
                          )}
                        </div>
                      </div>

                      <div className="dash-tabs">
                        {META_TABS.map(t => (
                          <button
                            key={t.key}
                            className={`dash-tab-btn${activeMetaTab === t.key ? " active" : ""}`}
                            onClick={() => setActiveMetaTab(t.key)}
                          >
                            {t.label}
                          </button>
                        ))}
                      </div>

                      <div className="dash-tab-body">
                        {activeMetaTab === "adAccounts" && (
                          Object.keys(fbAccounts).length === 0 ? (
                            <div className="campaigns-empty campaigns-empty-compact">
                              <p>No ad accounts found</p>
                              <span>Ad accounts on your Facebook Business account will show up here</span>
                            </div>
                          ) : (
                            <div className="ad-accounts-grid">
                              {Object.entries(fbAccounts).map(([id, account]) => (
                                <div
                                  key={id}
                                  className={`ad-account-item${selectedAdAccount === id ? " selected" : ""}`}
                                  onClick={() => switchAdAccount(id)}
                                >
                                  <div className="ad-account-avatar">
                                    {(account.name || "A")[0].toUpperCase()}
                                  </div>
                                  <div className="ad-account-info">
                                    <div className="ad-account-info-top">
                                      <span className="ad-account-item-name">{account.name}</span>
                                      {selectedAdAccount === id && <span className="ad-account-active-badge">Default</span>}
                                      {account.balance != null && (
                                        <span className="ad-account-item-balance">${(parseFloat(account.balance) / 100).toFixed(2)}</span>
                                      )}
                                    </div>
                                    <div className="ad-account-info-row">
                                      <span className="ad-account-item-id">{id}</span>
                                      <span
                                        className={`payment-status ${account.hasPaymentMethod ? "payment-ok" : "payment-missing"}`}
                                        title={account.hasPaymentMethod ? (account.paymentDisplay || "Payment active") : "No payment method"}
                                      >
                                        <span className="payment-dot" />
                                        {account.hasPaymentMethod ? "Active" : "No card"}
                                      </span>
                                      <a
                                        href={`https://business.facebook.com/billing_hub/payment_methods?asset_id=${id.replace("act_", "")}`}
                                        target="_blank" rel="noopener noreferrer"
                                        onClick={(e) => e.stopPropagation()}
                                        className="manage-payment-link"
                                      >Manage Payment Method</a>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )
                        )}

                        {activeMetaTab === "pages" && (
                          fbPages.length === 0 ? (
                            <div className="campaigns-empty campaigns-empty-compact">
                              <FaFileAlt style={{ fontSize: "1.3rem", color: "#94a3b8" }} />
                              <p>No Facebook Pages found</p>
                              <span>Pages connected to your Facebook account will show up here</span>
                            </div>
                          ) : (
                            <div className="cards-grid">
                              {fbPages.map((page) => (
                                <div key={page.id} className="page-card">
                                  <div className="page-card-header">
                                    {page.picture
                                      ? <img src={page.picture} alt={page.name} className="page-card-pic" />
                                      : <div className="page-card-pic-fallback">{page.name?.[0]?.toUpperCase() || "P"}</div>
                                    }
                                    <div>
                                      <div className="page-card-name">{page.name}</div>
                                      <div className="page-card-category">{page.category || "Page"}</div>
                                    </div>
                                  </div>
                                  <div className="page-card-stats">
                                    <div className="page-stat">
                                      <div className="page-stat-value">{(page.fanCount || 0).toLocaleString()}</div>
                                      <div className="page-stat-label">Likes</div>
                                    </div>
                                    <div className="page-stat">
                                      <div className="page-stat-value">{(page.followersCount || 0).toLocaleString()}</div>
                                      <div className="page-stat-label">Followers</div>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )
                        )}

                        {activeMetaTab === "instagram" && (
                          instagramAccounts.length === 0 ? (
                            <div className="campaigns-empty campaigns-empty-compact">
                              <FaInstagram style={{ fontSize: "1.3rem", color: "#94a3b8" }} />
                              <p>No Instagram accounts found</p>
                              <span>Instagram business accounts connected to your Pages will show up here</span>
                            </div>
                          ) : (
                            <div className="cards-grid">
                              {instagramAccounts.map((account) => (
                                <div key={account.id} className="ig-card">
                                  <div className="page-card-header">
                                    {account.profile_picture_url
                                      ? <img src={account.profile_picture_url} alt={account.username} className="ig-card-pic" />
                                      : <div className="ig-card-pic-fallback">{account.username?.[0]?.toUpperCase() || "I"}</div>
                                    }
                                    <div>
                                      <div className="ig-card-username">@{account.username || account.name || "Instagram"}</div>
                                      <div className="ig-card-sub">Connected to {account.connectedPageName || "Facebook Page"}</div>
                                    </div>
                                  </div>
                                  <div className="page-card-stats">
                                    <div className="page-stat">
                                      <div className="page-stat-value">{(account.followers_count || 0).toLocaleString()}</div>
                                      <div className="page-stat-label">Followers</div>
                                    </div>
                                    <div className="page-stat">
                                      <div className="page-stat-value">{(account.media_count || 0).toLocaleString()}</div>
                                      <div className="page-stat-label">Posts</div>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )
                        )}
                      </div>
                    </>
                  )}
                </div>

                </div> {/* end dash-overview-panel */}
                </div> {/* end dash-inner */}
              </>
            )}

            {wizardOpen && <CreateCampaignWizard initialData={wizardInitialData} onClose={() => { setWizardOpen(false); setWizardInitialData(null); }} />}

            {/* Feedback popup */}
            {showFeedback && (
              <div className="feedback-overlay" onClick={() => setShowFeedback(false)}>
                <div className="feedback-modal" onClick={e => e.stopPropagation()}>
                  <div className="feedback-header">
                    <div>
                      <div className="feedback-title">Help & Support</div>
                      <div className="feedback-sub">Send a message to matthew@adsoar.cc</div>
                    </div>
                    <button className="feedback-close" onClick={() => setShowFeedback(false)}>×</button>
                  </div>
                  {feedbackSent ? (
                    <div className="feedback-sent">
                      <div className="feedback-sent-icon">✓</div>
                      <div className="feedback-sent-msg">Message sent! We'll get back to you soon.</div>
                    </div>
                  ) : (
                    <div className="feedback-body">
                      <textarea
                        className="feedback-textarea"
                        placeholder="Describe your issue or question..."
                        value={feedbackMsg}
                        onChange={e => setFeedbackMsg(e.target.value)}
                        rows={5}
                      />
                      <button
                        className="feedback-send-btn"
                        disabled={!feedbackMsg.trim()}
                        onClick={() => {
                          window.open(`mailto:matthew@adsoar.cc?subject=Adsoar Support Request&body=${encodeURIComponent(feedbackMsg)}`);
                          setFeedbackSent(true);
                        }}
                      >
                        Send Message
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}
            {currentView === "manageCampaigns" && <ManageCampaigns onCreateCampaign={(data) => { setWizardInitialData(data || null); setWizardOpen(true); }} />}
            {currentView === "audiences" && <AudiencesPage />}
            {currentView === "reports" && <ReportsPage />}
            {currentView === "billing" && <BillingPage currentUser={currentUser} />}
            {currentView === "settings" && <SettingsPage key={settingsDefaultTab} defaultTab={settingsDefaultTab} onConnectFacebook={connectFacebook} currentUser={currentUser} />}
          </div>
        </div>
      </div>
    </>
  );
};

export default Dashboard;
